/**
 * KidCard — one row in the dashboard per child.
 *
 * Layout (matches mockups/dashboard.html):
 * ┌────────────────────────────────────────────────────────┐
 * │  [photo]  Name                See all 25 perspectives →│
 * │           6 years old · Born Jun 15, 2019              │
 * ├────────────────────────────────────────────────────────┤
 * │  ●●●●●●●○●●●●●●●●●●        11                          │
 * │  (dot grid)                summers before she turns 18 │
 * ├────────────────────────────────────────────────────────┤
 * │  [coral]  3    Christmases with a believer   [meter]   │
 * │  [honey]  ~900 bedtime stories ahead          [meter]  │
 * │  [amber]  1,924 family dinners left           [meter]  │
 * ├────────────────────────────────────────────────────────┤
 * │  ★ 5 pinned  ·  20 more in Explore                     │
 * └────────────────────────────────────────────────────────┘
 *
 * Hero is ALWAYS `eighteen_summers` (Decision 1, Phase 5 kickoff).
 * Stat bars show the top 3 pinned perspectives that are NOT
 * `eighteen_summers` (Decision 2). Partial-birthday children get a
 * "partial birthday" pill next to the age.
 */

import { Link } from "react-router-dom";

import { usePhotoURL } from "@/lib/usePhotoURL";
import type { Child } from "@/features/children/useChildren";
import {
  useChildPerspectives,
  type ComputedPerspective,
} from "@/features/perspectives/usePerspectives";
import {
  EighteenSummersDotGrid,
  NumberHero,
  StatBar,
} from "@/features/perspectives/primitives";

interface KidCardProps {
  child: Child;
}

const STAT_BAR_TONES = ["coral", "honey", "amber"] as const;
const MAX_STAT_BARS = 3;

export function KidCard({ child }: KidCardProps) {
  const { url: photoUrl } = usePhotoURL(child.id);
  const { data: perspectives, isLoading, error } = useChildPerspectives(child.id);

  const age = computeAge(child);
  const birthLabel = formatBirthLabel(child);
  const isEstimated =
    child.birth_month === null || child.birth_day === null;

  // 18 Summers always hero; filter it out of the stat-bar candidates.
  const eighteen =
    perspectives?.find((p) => p.slug === "eighteen_summers") ?? null;
  const statCandidates = (perspectives ?? [])
    .filter((p) => p.slug !== "eighteen_summers" && p.pinned && !p.expired)
    .slice(0, MAX_STAT_BARS);
  const pinnedCount = (perspectives ?? []).filter((p) => p.pinned).length;
  const totalVisible = perspectives?.length ?? 0;
  const exploreMoreCount = Math.max(0, totalVisible - pinnedCount);

  return (
    <article className="relative rounded-lg bg-surface border border-edge shadow-sm px-4 md:px-8 py-5 md:py-6 flex flex-col gap-4 md:gap-5 animate-cardRise">
      {/* Header: photo + identity + drill link */}
      <header className="flex items-start gap-3 md:gap-5">
        <div className="w-14 h-14 md:w-20 md:h-20 rounded-md bg-bg border border-edge overflow-hidden flex items-center justify-center shrink-0">
          {photoUrl === null ? (
            <div className="font-serif text-[24px] md:text-[32px] text-coral-deep">
              {child.name.slice(0, 1)}
            </div>
          ) : (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-[24px] md:text-[32px] text-ink leading-tight truncate">
            {child.name}
          </h2>
          <div className="mt-1 flex items-center flex-wrap gap-2">
            <span className="font-sans text-[13px] text-ink-muted">
              {age} years old · Born {birthLabel}
            </span>
            {isEstimated && (
              <span className="font-sans text-[10px] uppercase tracking-[0.14em] font-semibold text-coral-deep bg-coral-soft/60 border border-coral-deep/20 rounded-pill px-2 py-0.5">
                partial birthday
              </span>
            )}
          </div>
        </div>

        <Link
          to={`/children/${child.id}/perspectives`}
          className="shrink-0 hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink-soft border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber transition-colors"
        >
          See all 25 perspectives
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </Link>
      </header>

      {/* Hero: 18 Summers dot grid + number */}
      {isLoading ? (
        <div className="h-[96px] flex items-center">
          <span className="font-sans text-[13px] italic text-ink-muted">
            Computing perspectives…
          </span>
        </div>
      ) : error !== null && error !== undefined ? (
        <div className="font-sans text-[13px] text-coral-deep">
          Couldn't load perspectives. Refresh to try again.
        </div>
      ) : eighteen !== null ? (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
          <EighteenSummersDotGrid
            used={(eighteen.metadata.used as number | undefined) ?? 0}
            total={(eighteen.metadata.total as number | undefined) ?? 18}
            label={`${eighteen.value} of 18 summers remaining`}
          />
          <div className="flex flex-col">
            <NumberHero
              value={eighteen.value}
              confidence={eighteen.confidence}
              size="hero"
            />
            <div className="mt-1 font-serif italic text-[13px] md:text-[14px] text-ink-muted max-w-[260px]">
              {eighteen.caption}
            </div>
          </div>
        </div>
      ) : null}

      {/* Three stat bars for the top pinned perspectives */}
      {statCandidates.length > 0 && (
        <div className="flex flex-col gap-3">
          {statCandidates.map((perspective, index) => (
            <StatBar
              key={perspective.slug}
              value={perspective.value}
              label={perspective.caption}
              confidence={perspective.confidence}
              percentRemaining={extractPercentRemaining(perspective)}
              tone={STAT_BAR_TONES[index % STAT_BAR_TONES.length] ?? "coral"}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      <footer className="flex items-center justify-between border-t border-edge-soft pt-4 mt-1">
        <div className="flex items-center gap-2 font-sans text-[12px] text-ink-muted">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="text-[var(--rwar-red)]"
            aria-hidden="true"
          >
            <path d="M14 2l-1 7 4 3v3h-5l-1 7h-2l-1-7H3v-3l4-3-1-7z" />
          </svg>
          {pinnedCount} pinned · {exploreMoreCount} more in Explore
        </div>
        <Link
          to={`/children/${child.id}/perspectives`}
          className="md:hidden font-sans text-[12px] text-coral-deep font-medium"
        >
          See all →
        </Link>
      </footer>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeAge(child: Child): number {
  // Mirror the backend birth resolver for the "age in years" number.
  const year = child.birth_year;
  const month = child.birth_month ?? 6;
  const day = child.birth_day ?? 21;
  const now = new Date();
  const birth = new Date(year, month - 1, day);
  let age = now.getFullYear() - birth.getFullYear();
  const beforeBirthdayThisYear =
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
  if (beforeBirthdayThisYear) age -= 1;
  return age;
}

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function formatBirthLabel(child: Child): string {
  if (child.birth_month === null) return String(child.birth_year);
  const monthName = MONTH_LABELS[child.birth_month - 1] ?? String(child.birth_month);
  if (child.birth_day === null) return `${monthName} ${child.birth_year}`;
  return `${monthName} ${child.birth_day}, ${child.birth_year}`;
}

function extractPercentRemaining(
  perspective: ComputedPerspective,
): number | undefined {
  const used = perspective.metadata.used;
  const total = perspective.metadata.total;
  if (typeof used !== "number" || typeof total !== "number" || total === 0) {
    return undefined;
  }
  return Math.round(((total - used) / total) * 100);
}
